// src/pages/SalesTracking.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Badge } from 'react-bootstrap';
import { TrendingUp, Car, DollarSign, Award } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmt = v => `PKR ${Number(v || 0).toLocaleString()}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <Card.Body className="d-flex align-items-center gap-3 py-3">
        <div style={{ background: `${color}22`, borderRadius: 10, padding: 10 }}>
          <Icon size={20} style={{ color }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</div>
          <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{value}</div>
        </div>
      </Card.Body>
    </Card>
  );
}

function SalesSkeleton() {
  return (
    <div className="container-fluid px-4 py-4">
      <div className="crm-skeleton-pulse mb-3" style={{ width: 180, height: 28 }} />
      <div className="crm-skeleton-pulse mb-4" style={{ width: 320, height: 16 }} />
      <div className="row g-3 mb-4">
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} className="col-md-2 col-sm-6">
            <div className="card border-0 p-3" style={{ height: 75 }}>
              <div className="crm-skeleton-pulse mb-2" style={{ width: '60%', height: 12 }} />
              <div className="crm-skeleton-pulse" style={{ width: '80%', height: 20 }} />
            </div>
          </div>
        ))}
      </div>
      <div className="card border-0 p-3 mb-4" style={{ height: 180 }}>
        <div className="crm-skeleton-pulse mb-3" style={{ width: 150, height: 18 }} />
        <div className="crm-skeleton-pulse mb-2" style={{ width: '100%', height: 20 }} />
        <div className="crm-skeleton-pulse mb-2" style={{ width: '100%', height: 20 }} />
        <div className="crm-skeleton-pulse" style={{ width: '100%', height: 20 }} />
      </div>
    </div>
  );
}

export default function SalesTracking() {
  const { user } = useAuth();
  const [myStats,    setMyStats]    = useState(null);
  const [teamStats,  setTeamStats]  = useState([]);
  const [companyStats,setCompanyStats]= useState(null);
  const [loading,    setLoading]    = useState(true);

  const MONTHLY_TARGET = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes] = await Promise.all([api.get('/sales/me')]);
      setMyStats(meRes.data);

      if (user?.role === 'Manager' || user?.role === 'Admin') {
        const teamRes = await api.get('/sales/team');
        setTeamStats(Array.isArray(teamRes.data) ? teamRes.data : []);
      }
      if (user?.role === 'Admin') {
        const allRes = await api.get('/sales/all');
        setCompanyStats(allRes.data);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user?.role]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <SalesSkeleton />;

  const pct = myStats ? Math.min(100, Math.round((myStats.monthlyCarsSold / MONTHLY_TARGET) * 100)) : 0;

  return (
    <div className="container-fluid px-2 py-1">
      <div className="text-muted small mb-3">Personal sales conversion milestones and corporate revenue performance.</div>

      {/* My Stats */}
      {myStats && (
        <>
          <h5 className="fw-semibold mb-3">My Performance</h5>
          <div className="row g-3 mb-4">
            <div className="col-md-2 col-sm-6"><StatCard label="Cars Sold (Month)" value={myStats.monthlyCarsSold} icon={Car} color="#3b82f6" /></div>
            <div className="col-md-2 col-sm-6"><StatCard label="Lifetime Cars Sold" value={myStats.lifetimeCarsSold} icon={Award} color="#8b5cf6" /></div>
            <div className="col-md-3 col-sm-6"><StatCard label="Total Revenue" value={fmt(myStats.totalRevenue)} icon={DollarSign} color="#22c55e" /></div>
            <div className="col-md-3 col-sm-6"><StatCard label="Total Profit" value={fmt(myStats.totalProfit)} icon={TrendingUp} color="#f59e0b" /></div>
            <div className="col-md-2 col-sm-6"><StatCard label="Avg Profit/Sale" value={fmt(myStats.avgProfit)} icon={TrendingUp} color="#ec4899" /></div>
          </div>

          {/* Target progress */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between mb-1">
                <span className="small fw-semibold">Monthly Target Progress</span>
                <span className="small text-muted">{myStats.monthlyCarsSold} / {MONTHLY_TARGET} cars ({pct}%)</span>
              </div>
              <div style={{ height: 10, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#3b82f6', transition: 'width 0.5s ease', borderRadius: 99 }} />
              </div>
            </Card.Body>
          </Card>

          {/* Monthly trend */}
          {myStats.monthlySales?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent fw-semibold">Monthly Trend (Last 6 months)</Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                  <thead className="table-header"><tr><th>Month</th><th>Cars Sold</th><th>Revenue</th><th>Profit</th></tr></thead>
                  <tbody>
                    {myStats.monthlySales.map(m => (
                      <tr key={`${m.year}-${m.month}`}>
                        <td>{MONTHS[(m.month||1)-1]} {m.year}</td>
                        <td className="fw-semibold">{m.carsSold}</td>
                        <td>{fmt(m.revenue)}</td>
                        <td className="text-success fw-semibold">{fmt(m.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Recent sales */}
          {myStats.recentSales?.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent fw-semibold">Recent Sales</Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                  <thead className="table-header"><tr><th>Date</th><th>Lead</th><th>Brand</th><th>Sale Value</th><th>Profit</th><th>Notes</th></tr></thead>
                  <tbody>
                    {myStats.recentSales.map(s => (
                      <tr key={s.id}>
                        <td>{fmtDate(s.saleDate)}</td>
                        <td>{s.leadName || '—'}</td>
                        <td>{s.brandName ? <Badge bg="primary" style={{ background: s.brandColor }}>{s.brandName}</Badge> : '—'}</td>
                        <td className="fw-semibold">{fmt(s.saleValue)}</td>
                        <td className="text-success fw-semibold">{fmt(s.profitGenerated)}</td>
                        <td className="text-muted small">{s.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* Team Stats */}
      {teamStats.length > 0 && (
        <>
          <h5 className="fw-semibold mb-3 mt-2">Team Performance</h5>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                <thead className="table-header">
                  <tr><th>Name</th><th>Role</th><th>Cars (Month)</th><th>Lifetime</th><th>Revenue</th><th>Profit</th><th>Avg Profit</th></tr>
                </thead>
                <tbody>
                  {teamStats.map(a => (
                    <tr key={a.agentId}>
                      <td className="fw-semibold">{a.agentName}</td>
                      <td><Badge bg="secondary">{a.role}</Badge></td>
                      <td>{a.monthlyCarsSold}</td>
                      <td>{a.lifetimeCarsSold}</td>
                      <td>{fmt(a.totalRevenue)}</td>
                      <td className="text-success fw-semibold">{fmt(a.totalProfit)}</td>
                      <td>{fmt(a.avgProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Company / Brand breakdown */}
      {companyStats && (
        <>
          <h5 className="fw-semibold mb-3">Company Stats</h5>
          <div className="row g-3 mb-4">
            <div className="col-md-4"><StatCard label="Total Cars Sold" value={companyStats.company.totalCarsSold} icon={Car} color="#3b82f6" /></div>
            <div className="col-md-4"><StatCard label="Total Revenue" value={fmt(companyStats.company.totalRevenue)} icon={DollarSign} color="#22c55e" /></div>
            <div className="col-md-4"><StatCard label="Total Profit" value={fmt(companyStats.company.totalProfit)} icon={TrendingUp} color="#f59e0b" /></div>
          </div>
          {companyStats.byBrand?.length > 0 && (
            <div className="row g-3 mb-4">
              {companyStats.byBrand.map(b => (
                <div className="col-md-4" key={b.id}>
                  <Card className="border-0 shadow-sm" style={{ borderTop: `4px solid ${b.color}` }}>
                    <Card.Body>
                      <div className="fw-semibold mb-1" style={{ color: b.color }}>{b.brandName}</div>
                      <div className="small text-muted">{b.carsSold} cars · {fmt(b.revenue)} revenue · {fmt(b.profit)} profit</div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
